import * as React from 'react'

import type { Form } from 'factorial-form'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import { UserSelect } from 'modules/automations/components/blocks/UserSelect'
import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { ConditionTypes } from 'modules/automations/lib/enums'
import { ITriggerCondition, Trigger } from 'modules/automations/store/models/Trigger'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import CustomFieldSelect from './CustomFieldSelect'
import FolderSelect from './FolderSelect'
import { RegularSelect } from './RegularSelect'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
  condition?: ITriggerCondition
  extensionFeatureHasConditions: boolean
  contractVersions: ContractVersions
}

export default function ValueSelect({
  disabled,
  form,
  operation,
  loadingCustomFields,
  customFieldsCollection,
  condition,
  extensionFeatureHasConditions,
  contractVersions,
}: Props) {
  const valueSelect = () => {
    if (condition?.key === 'folder_id') {
      return <FolderSelect field={form.get('value')} />
    }
    switch (condition?.type) {
      case ConditionTypes.SingleChoice:
        return (
          <RegularSelect
            disabled={disabled}
            form={form}
            operation={operation}
            condition={condition}
          />
        )
      case ConditionTypes.Boolean:
      case ConditionTypes.Identifier:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
      case ConditionTypes.User:
        return (
          <UserSelect
            disabled={disabled}
            field={form.get('value')}
            contractVersions={contractVersions}
          />
        )
      case ConditionTypes.Date:
        return (
          <DayPicker label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />
        )
      default:
        return null
    }
  }

  const oldValueSelect = () => {
    const attribute = operation.attribute

    switch (attribute) {
      case Trigger.Attributes.employee_id:
      case Trigger.Attributes.manager_id:
      case Trigger.Attributes.timeoff_manager_id:
      case Trigger.Attributes.assignee_id:
      case Trigger.Attributes.author_id:
        return (
          <UserSelect
            useEmployee={Trigger.Attributes.employee_id === operation.attribute}
            disabled={disabled}
            field={form.get('value')}
            contractVersions={contractVersions}
          />
        )

      case Trigger.Attributes.birthday:
      case Trigger.Attributes.startsOn:
      case Trigger.Attributes.endsOn:
      case Trigger.Attributes.trialPeriodEndsOn:
        return (
          <DayPicker label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />
        )

      case Trigger.Attributes.jobTitle:
      case Trigger.Attributes.salaryAmount:
      case Trigger.Attributes.workingHours:
        return <Text label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />

      case Trigger.Attributes.spainProfessionalCategories:
      case Trigger.Attributes.educationLevel:
      case Trigger.Attributes.cotizationGroup:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
      case ContractVersion.CustomContractAttribute(attribute):
        return <Text label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />

      case Trigger.Attributes.custom_fields:
      case Trigger.Attributes.custom_resources:
        return (
          <CustomFieldSelect
            disabled={disabled}
            form={form}
            operation={operation}
            loadingCustomFields={loadingCustomFields}
            customFieldsCollection={customFieldsCollection}
          />
        )

      default:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
    }
  }

  return extensionFeatureHasConditions ? valueSelect() : oldValueSelect()
}
